// libraries
import * as ISchema from "#webapp/src/__schema__";
import { useQuery } from "@apollo/client";
import { compact, map, uniq } from "lodash/fp";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
//util
import { generateRangeMinMax, getFetchDatesForPeriod } from "#webapp/src/util/team-health.util";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { transform } from "@clockwise/client-commons/src/util/transform.util";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
// schema and state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { IRendererContainer, IRendererProps, OrgForCalendarProfile } from "./CalendarProfileTypes";
import { GetCalendarProfileDocument } from "./__generated__/CalenderProfileRenderer.generated";
// components
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { CalendarProfile } from "./CalendarProfile";
// assets and CSS

const fetchDates = getFetchDatesForPeriod();
const { rangeMax: rMax, rangeMin: rMin } = generateRangeMinMax(fetchDates.start, fetchDates.end);

const CalendarProfileRendererCmpt = ({ org, calendarId, environment }: IRendererProps) => {
  const [orgWithProfile, setOrgWithProfile] = useState<OrgForCalendarProfile | null>(null);
  const [rangeMin, setRangeMin] = useState(rMin);
  const [rangeMax, setRangeMax] = useState(rMax);
  const [calendarIds, setCalendarIds] = useState<string[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>("");

  useEffect(() => {
    // this check is added as a way to prevent requerying should calendarIds end up being the exact same, this way we only change calenderIds if its actually different
    const willCalendarIdsNotReallyChange =
      calendarIds.length === 1 && calendarIds[0] === calendarId;
    if (!willCalendarIdsNotReallyChange) {
      setCalendarIds([calendarId]);
    }
    setSelectedCalendarId(calendarId);

    // Adding to prevent unnessesary overcalling, here calendarIds is just used as a precautionary check
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarId]);

  const onEditRange = (
    newMaxRange: ISchema.IDayTimeSlotInput,
    newMinRange: ISchema.IDayTimeSlotInput,
  ) => {
    setRangeMin(newMinRange);
    setRangeMax(newMaxRange);
  };

  useQuery<{ org?: OrgForCalendarProfile }>(GetCalendarProfileDocument, {
    variables: {
      calendarId: selectedCalendarId,
      orgId: org.id,
      rangeMax,
      rangeMin,
      timeZone: getRenderTimeZone(),
      calendarIds: calendarIds,
    },
    onCompleted: (res) => {
      if (res.org) {
        const orgFromResponse = getValue(res?.org, "Org");
        if (orgFromResponse && orgFromResponse.__typename === "Org") {
          setOrgWithProfile(res.org);
          const collaboratorFromRes: string[] = transform(
            getValue(orgFromResponse.collaboratorStatsErrorable)?.topCollaborators ?? [],
            map((ea) => ea?.calendarId),
            compact,
            uniq,
          );

          if (collaboratorFromRes.length) {
            setCalendarIds(collaboratorFromRes);
          }
        }
      }
    },
    skip: !rangeMin || !rangeMax || !calendarIds.length || !selectedCalendarId,
  });

  if (!orgWithProfile) {
    return (
      <div className="cw-relative cw-mb-5">
        <div className="cw-flex cw-flex-col cw-inset-0 cw-absolute cw-items-center cw-justify-center cw-min-h-[300px]">
          <CircularProgress />
        </div>
      </div>
    );
  } else {
    return (
      <CalendarProfile
        org={orgWithProfile}
        calendarId={selectedCalendarId}
        onEditRange={onEditRange}
        relayEnvironment={environment}
        onChangeSelectedCalendarId={(calendarId: string) => {
          setSelectedCalendarId(calendarId);
        }}
      />
    );
  }
};

export const CalendarProfileRenderer = connect(
  (state: IReduxState, _ownProps: IRendererContainer) => ({
    calendarId: state.webExtension.activeViewCalendarProfile.calendarId,
  }),
  {},
)(CalendarProfileRendererCmpt);
